import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';

import { supabase } from '../libs/supabase';

// RTK Query error format
export interface SupabaseQueryError {
  status: 'NOT_FOUND' | 'FETCH_ERROR' | 'BAD_REQUEST';
  error: string;
}

export interface SupabaseQueryResponse<T> {
  data?: T;
  error?: SupabaseQueryError;
}

// Handle common Supabase errors
export const handleSupabaseError = (
  error: PostgrestError,
  context?: string
): SupabaseQueryError => {
  if (error.code === 'PGRST116') {
    return {
      status: 'NOT_FOUND',
      error: context ? `${context} not found` : 'Record not found',
    };
  }
  return {
    status: 'FETCH_ERROR',
    error: error.message,
  };
};

// Functional approach for RTK Query queryFn
export const sbq =
  <T, P = any>(fn: (sb: SupabaseClient, params: P) => Promise<T>, context?: string) =>
  async (params: P): Promise<{ data: T } | { error: SupabaseQueryError }> => {
    try {
      const data = await fn(supabase, params);
      return { data };
    } catch (e: any) {
      // Handle Supabase errors
      if (e && e.code) {
        return { error: handleSupabaseError(e, context) };
      }
      return { error: { status: 'FETCH_ERROR', error: `${context ?? ''}: ${String(e)}` } };
    }
  };

// Filter out soft-deleted items
export const filterDeleted = <T extends { is_deleted?: boolean }>(items: T[]): T[] => {
  return items.filter((item) => item.is_deleted !== true);
};

// Helper to throw on Supabase errors (for use with sbq)
export const throwOnError = <T>(result: {
  data: T | null;
  error: PostgrestError | null;
}): NonNullable<T> => {
  if (result.error) {
    throw result.error;
  }
  if (result.data === null || result.data === undefined) {
    throw new Error('No data returned from query');
  }
  return result.data as NonNullable<T>;
};
